"use client";

import { cn } from "@/lib/utils";
import { Star } from "lucide-react";
import { useState } from "react";

interface StarRatingProps {
	initialRating?: number;
	onRatingChange?: (rating: number) => void;
	className?: string;
	starSize?: number;
	showTotal?: boolean;
	readonly?: boolean;
}

export function Rating({
	initialRating = 0,
	onRatingChange,
	className,
	starSize = 3,
	showTotal = false,
	readonly = false,
}: StarRatingProps) {
	const [rating, setRating] = useState(initialRating);
	const [hoverRating, setHoverRating] = useState(0);

	const handleStarClick = (starIndex: number, isHalf: boolean) => {
		if (readonly) return;
		const newRating = isHalf ? starIndex + 0.5 : starIndex + 1;
		setRating(newRating);
		onRatingChange?.(newRating);
	};

	const handleStarHover = (starIndex: number, isHalf: boolean) => {
		if (readonly) return;
		const newHoverRating = isHalf ? starIndex + 0.5 : starIndex + 1;
		setHoverRating(newHoverRating);
	};

	const handleMouseLeave = () => {
		if (readonly) return;
		setHoverRating(0);
	};

	const getStarFill = (starIndex: number) => {
		const currentRating = hoverRating || rating;

		if (currentRating >= starIndex + 1) {
			return "full";
		}
		if (currentRating >= starIndex + 0.5) {
			return "half";
		}
		return "empty";
	};

	return (
		<div className={cn("flex items-center gap-0.75", className)}>
			{[0, 1, 2, 3, 4].map((starIndex) => {
				const fillType = getStarFill(starIndex);

				return (
					<div
						key={starIndex}
						className={cn("relative", !readonly && "cursor-pointer")}
						onMouseLeave={handleMouseLeave}
					>
						{!readonly && (
							<>
								{/* 左半边 - 半星 */}
								<div
									className="absolute left-0 top-0 w-1/2 h-full z-10"
									onClick={() => handleStarClick(starIndex, true)}
									onMouseEnter={() => handleStarHover(starIndex, true)}
								/>

								{/* 右半边 - 整星 */}
								<div
									className="absolute right-0 top-0 w-1/2 h-full z-10"
									onClick={() => handleStarClick(starIndex, false)}
									onMouseEnter={() => handleStarHover(starIndex, false)}
								/>
							</>
						)}

						{/* 星星背景 */}
						<Star
							className={cn(
								"text-c3 transition-colors duration-150",
								`w-${starSize} h-${starSize}`,
							)}
							fill="currentColor"
						/>

						{/* 星星填充 */}
						<div className="absolute top-0 left-0 overflow-hidden">
							<Star
								className={cn(
									"text-c4 transition-colors duration-150",
									`w-${starSize} h-${starSize}`,
									fillType === "full" && "text-c4",
									fillType === "half" && "text-c4",
									fillType === "empty" && "text-transparent",
								)}
								fill="currentColor"
								style={{
									clipPath:
										fillType === "half"
											? "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
											: fillType === "full"
												? "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
												: "polygon(0 0, 0 0, 0 100%, 0 100%)",
								}}
							/>
						</div>
					</div>
				);
			})}

			<span className=" text-xs text-t3 min-w-5 text-right">
				{(hoverRating || rating).toFixed(1)}
				{showTotal && " / 5.0"}
			</span>
		</div>
	);
}
